import {
  Message as MonolithMessage,
  Severity,
  Type,
} from '@app/types/monolith/cart';
import * as constants from '@redbubble/design-system/src/constants';
import { ObjectValues } from '../../types';
import { type Message, MessageSeverity, MessageType } from './types';

const SHIPPING_MESSAGE_TYPES: MessageType[] = [
  MessageType.FreeShippingOnProductApplied,
  MessageType.FlatRateShippingApplied,
];

export const isShippingMessage = ({ type }: Message) =>
  Boolean(type && SHIPPING_MESSAGE_TYPES.includes(type));

export const isNotShippingMessage = (message: Message) =>
  !isShippingMessage(message);

export const isFlatRateShippingApplied = ({ type }: Message) =>
  Boolean(type && type === MessageType.FlatRateShippingApplied);

export const isExpiredProductRemoval = ({ type }: Message) =>
  Boolean(type && type === MessageType.ExpiredProductRemoval);

export const isNonsaleableProductRemoval = ({ type }: Message) =>
  Boolean(type && type === MessageType.NonsaleableProductRemoval);

export const isAppliedMessage = ({ applied }: Message) => applied;

export const messageSeverityToDesignSystemConstant: Record<
  MessageSeverity,
  // XXX(jj): redbubble/design-system/constants do not resolve to a type,
  // so we cannot extract specific constants.
  // I am deliberately not defining a type here as I do not want to provide a
  // false sense of certainty
  any
> = {
  [MessageSeverity.Error]: constants.ERROR,
  [MessageSeverity.Info]: constants.INFORMATION,
  [MessageSeverity.Success]: constants.SUCCESS,
  [MessageSeverity.Warning]: constants.WARNING,
};

const typeMap: Record<ObjectValues<typeof Type>, MessageType> = {
  [Type.ProductWarning]: MessageType.ProductWarning,
  [Type.DiscountSitewide]: MessageType.DiscountSitewide,
  [Type.DiscountVolume]: MessageType.DiscountVolume,
  [Type.DiscountShipping]: MessageType.DiscountShipping,
  [Type.FreeShippingOnProductApplied]: MessageType.FreeShippingOnProductApplied,
  [Type.FlatRateShippingApplied]: MessageType.FlatRateShippingApplied,
  [Type.NonsaleableProductRemoval]: MessageType.NonsaleableProductRemoval,
  [Type.ExpiredProductRemoval]: MessageType.ExpiredProductRemoval,
};

const severityMap: Record<ObjectValues<typeof Severity>, MessageSeverity> = {
  [Severity.Info]: MessageSeverity.Info,
  [Severity.Success]: MessageSeverity.Success,
  [Severity.Warning]: MessageSeverity.Warning,
  [Severity.Error]: MessageSeverity.Error,
};

export const mapSeverity = (
  severity: ObjectValues<typeof Severity>
): MessageSeverity => severityMap[severity];

export const mapCartMessageFromMonolith = (
  message: MonolithMessage
): Message => {
  return {
    actionUrl: message.action_url,
    applied: message.applied,
    productName: message.product_name || undefined, // map null to undefined
    severity: mapSeverity(message.severity),
    text: message.text,
    type: typeMap[message.type],
  };
};
